import React, { useEffect, useState } from "react";
import MUITable from "Components/UI/Tables/MUITable";
import { Link } from "react-router-dom";
import RefreshBtn from "Components/UI/Tables/RefreshBtn";
import { createPrintedInvoicePrintFile, regretAccount } from "API/swesum";
import dayjs from "dayjs";
import useTableOptions from "Hooks/useTableOptions";
import { systemStatusOptions } from "utils/options/systemStatusOptions";
import { LoadingButton } from "@mui/lab";
import { Box } from "@mui/material";
import WriteOffAccountModal from "./Modals/WriteOffAccountModal";
import ConfirmModal from "Pages/AdminInterface/invoices/processInvoices/ConfirmModal";
import { useAuth } from "Hooks/useAuth";
import { useArticles } from "Context/Articles";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import CommonChip from "Components/UI/Tables/CommonChip";
import {
  getFortnoxReportBackStatusColor,
  getFortnoxReportBackStatusText,
} from "utils/options/FORTNOX_REPORTBACK_STATUS";

const createRows = (accounts, articles) => {
  return accounts.map((a) => {
    return [
      a.Id,
      a.BookingDate,
      a.EndCustomerArticle,
      a.InvoiceReference,
      a.Amount,
      a.OCR,
      a.FromAccount,
      a.ToAccount,
      a.Description,
      systemStatusOptions.find((status) => status.Value === a.SystemStatus)
        ?.Name,
      a.WriteOffId,
      a.InvoiceId,
      a.PrintedId,
      a.BookRefId,
      a.PaymentId,
      a.ExportFileId,
      a.BgMaxFileId,
      a.MatchText,
      a.Points,
      a.FortnoxPaymentState,
      a.Id,
    ];
  });
};

const columnsToHide = [
  "Id",
  "Debet",
  "Kredit",
  "Beskrivning",
  "Status",
  "InvoiceId",
  "PrintedId",
  "BookRefId",
  "PaymentId",
  "ExportFileId",
  "BgMaxFileId",
  "MatchText",
  "Poäng",
  "AvskrivningsId",
  "Återrapporterad",
];

export default function EndcustomerAccountsTableContainer({
  accounts,
  title,
  fetchAccounts,
  onRegretSuccess,
  onChangePage,
  onChangeRowsPerPage,
  currentPage,
  rowsPerPage,
  totalRows,
  onRowClick,
  endcustomerArticleMap,
}) {
  const { isAdmin } = useAuth();
  const { endCustomerArticleMap, fetchEndCustomerArticles } = useArticles();
  const { openSuccess, openError } = usePopupNotificationContext();
  const [rows, setRows] = useState(
    createRows(accounts ? accounts : [], endCustomerArticleMap)
  );
  const [loadingIds, setLoadingIds] = useState([]);
  const [writeOffModalOpen, setWriteOffModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [confirmRegretModalOpen, setConfirmRegretModalOpen] = useState(false);

  const { handleColumnShow, isColDisplayed, isNew } = useTableOptions(
    "EndCustomerEventTable"
  );

  useEffect(() => {
    if (endCustomerArticleMap === null) fetchEndCustomerArticles();
  }, []);


  useEffect(() => {
    if (isNew) {
      for (const col of columnsToHide) {
        handleColumnShow(col, "remove");
      }
    }
  }, [isNew]);

  useEffect(() => {
    const tempAccounts = accounts
      ? assignAccountArticle(
          assignInvoiceNumbers(accounts),
          endCustomerArticleMap
        )
      : [];
    const newRows = createRows(tempAccounts, endCustomerArticleMap);
    setRows(newRows);
  }, [accounts, endCustomerArticleMap]);

  const onPrintClick = async (accountId) => {
    setLoadingIds((prev) => [...prev, accountId]);

    try {
      var res = await createPrintedInvoicePrintFile(accountId);

      if (res.status === 201) {
        openSuccess("Utskrift lyckad", 2000);
      } else {
        openError("Kunde inte skriva ut", 2000);
        console.error(res);
      }
    } catch (error) {
      openError("Kunde inte skriva ut", 2000);
      console.error(error);
    } finally {
      setLoadingIds((prev) => prev.filter((id) => id !== accountId));
    }
  };

  //small function to ensure that parent and child accounts have the same invoice number, e.g. a reminder account and write off account.
  const assignInvoiceNumbers = (tempAccounts) => {
    const invoiceMap = new Map(tempAccounts.map((inv) => [inv.Id, inv]));

    tempAccounts.forEach((invoice) => {
      if (invoice.WriteOffId !== null) {
        const writeOffInvoice = invoiceMap.get(invoice.WriteOffId);

        if (writeOffInvoice) {
          writeOffInvoice.InvoiceReference = invoice.InvoiceReference;
        }
      }
    });

    return tempAccounts;
  };
  const handleOnRegret = (account) => {
    setSelectedAccount(account);
    setConfirmRegretModalOpen(true);
  };

  const executeRegret = async () => {
    const res = await regretAccount(selectedAccount.Id);
    
    setConfirmRegretModalOpen(false);
    if (res.status === 201) {
      openSuccess("Inbetalning ångrad", 2000);
      onRegretSuccess?.();
    } else {
      openError("Kunde inte ångra inbetalning", 2000);
      console.error(res);
    }
    return res;
  };

  const assignAccountArticle = (tempAccounts, endCustomerArticleMap) => {
    if (!endCustomerArticleMap) return tempAccounts;
    tempAccounts.forEach((account) => {
      if (account.EndCustomerArticle == null) {
        const mappedArticle =
          endCustomerArticleMap[account?.EndCustomerArticleId];
        if (mappedArticle) {
          account.EndCustomerArticle = mappedArticle;
        }
      }
    });

    return tempAccounts;
  };

  const columns = [
    {
      name: "Id",
      options: {
        display: isColDisplayed("Id"),
      },
    },
    {
      name: "Bokföringsdatum",
      options: {
        customBodyRender: (value) => {
          return dayjs(value).format("YYYY-MM-DD");
        },
        display: isColDisplayed("Bokföringsdatum"),
      },
    },
    {
      name: "Typ",
      options: {
        customBodyRender: (value) => {
          return value?.Name;
        },
        display: isColDisplayed("Typ"),
      },
    },
    {
      name: "Fakturanummer",
      options: {
        display: isColDisplayed("Fakturanummer"),
      },
    },
    {
      name: "Belopp",
      options: {
        display: isColDisplayed("Belopp"),
      },
    },
    {
      name: "OCR",
      options: {
        display: isColDisplayed("OCR"),
      },
    },
    {
      name: "Debet",
      options: {
        display: isColDisplayed("Debet"),
      },
    },
    {
      name: "Kredit",
      options: {
        display: isColDisplayed("Kredit"),
      },
    },
    {
      name: "Beskrivning",
      options: {
        display: isColDisplayed("Beskrivning"),
      },
    },
    {
      name: "Status",
      options: {
        display: isColDisplayed("Status"),
        filterList: ["Aktiv"],
      },
    },
    {
      name: "AvskrivningsId",
      options: {
        display: isColDisplayed("WriteOffId"),
      },
    },
    {
      name: "InvoiceId",
      options: {
        display: isColDisplayed("InvoiceId"),
        customBodyRender: (value) => {
          return <Link to={`/invoices/${value}`}>{value}</Link>;
        },
      },
    },
    {
      name: "PrintedId",
      options: {
        display: isColDisplayed("PrintedId"),
        customBodyRender: (value) => {
          return (
            value > -1 ?? (
              <Link to={`/endcustomerprinted/${value}`}>{value}</Link>
            )
          );
        },
      },
    },
    {
      name: "BookRefId",
      options: {
        display: isColDisplayed("BookRefId"),
        customBodyRender: (value) => {
          return <Link to={`/books/${value}`}>{value}</Link>;
        },
      },
    },
    {
      name: "PaymentId",
      options: {
        display: isColDisplayed("PaymentId"),
        customBodyRender: (value) => {
          return <Link to={`/endcustomerpaid/${value}`}>{value}</Link>;
        },
      },
    },
    {
      name: "ExportFileId",
      options: {
        display: isColDisplayed("ExportFileId"),
      },
    },
    {
      name: "BgMaxFileId",
      options: {
        display: isColDisplayed("BgMaxFileId"),
      },
    },
    {
      name: "MatchText",
      options: {
        display: isColDisplayed("MatchText"),
      },
    },
    {
      name: "Poäng",
      options: {
        display: isColDisplayed("Poäng"),
      },
    },
    {
      name: "Återrapporterad",
      options: {
        display: isColDisplayed("Återrapporterad"),
        customBodyRender: (value) => {
          if (value != null && value >= 0) {
            return (
              <CommonChip
                sx={{ minWidth: "5rem" }}
                labelName={getFortnoxReportBackStatusText(value)}
                color={getFortnoxReportBackStatusColor(value)}
              />
            );
          }
        },
      },
    },
    {
      name: "",
      options: {
        filter: false,
        customBodyRender: (value) => {
          const account = accounts.find(
            (account) => Number(account.Id) === Number(value)
          );

          const valsWriteOff = ["PÅMINNELSE1", "PÅMINNELSE2", "RÄNTEFAKTURA"];

          const valsPrint = [
            "KUNDFAKTURA",
            "PÅMINNELSE1",
            "PÅMINNELSE2",
            "KREDITFAKTURA",
            "RÄNTEFAKTURA",
          ];

          const valsDescription = ["Justering av felbokad inbetalning", "Felbokad inbetalning"];

          const valsReturn = ["INBETALNING"];
          
          const interestPaymentsTotal = accounts
            .filter(
              (a) =>
                a.EndCustomerArticle?.Name === "Inbetalning räntefaktura" &&
                a.SystemStatus === "1"
            )
            .reduce((sum, a) => sum + Math.abs(a.Amount), 0);
          
          const hasInterestPayment =
            account.EndCustomerArticle?.Name === "Räntefaktura" &&
            interestPaymentsTotal >= Math.abs(account.Amount);

          return (
            <Box display={"flex"} gap={2}>
              {isAdmin &&
              valsPrint.includes(account?.EndCustomerArticle?.Payrule) ? (
                <LoadingButton
                  variant="contained"
                  size="small"
                  loading={loadingIds.includes(account.Id)}
                  onClick={() => {
                    onPrintClick(account.Id);
                  }}
                  disabled={account.WriteOffId != null || hasInterestPayment}
                >
                  Skriv ut
                </LoadingButton>
              ) : null}
              {isAdmin &&
              valsWriteOff.includes(account?.EndCustomerArticle?.Payrule) ? (
                <LoadingButton
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setWriteOffModalOpen(true);
                    setSelectedAccount(account);
                  }}
                  disabled={account.WriteOffId != null || hasInterestPayment}
                >
                  Skriv av
                </LoadingButton>
              ) : null}
              {isAdmin &&
              valsReturn.includes(account?.EndCustomerArticle?.Payrule) && !valsDescription.includes(account.Description) && !account.Description.includes("ångrad") ? (
                <LoadingButton
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => handleOnRegret(account)}
                >
                  Ångra
                </LoadingButton>
              ): null}  
            </Box>
          );
        },
      },
    },
  ];

  const options = {
    selectableRowsHeader: true,
    selectableRowsHideCheckboxes: true,
    onViewColumnsChange: (changedColumn, action) => {
      handleColumnShow(changedColumn, action);
    },
    downloadOptions: {
      separator: ";",
      filename: "client_accounts.csv",
    },
    onChangePage: onChangePage
      ? (currentPage) => onChangePage(currentPage)
      : null,
    onChangeRowsPerPage: onChangeRowsPerPage
      ? (numberOfRows) => onChangeRowsPerPage(numberOfRows)
      : null,
    rowsPerPage: rowsPerPage,
    onRowClick: (_, rowMeta) => {
      if (onRowClick) {
        onRowClick(rows[rowMeta.dataIndex][0]);
      }
    },
    page: currentPage,
    count: totalRows,
    rowsPerPageOptions: [10, 30, 100],
    textLabels: {
      body: {
        noMatch: "",
      },
    },
    customToolbar: () => {
      return (
        <>
          <RefreshBtn />
        </>
      );
    },

    onDownload: (buildHead, buildBody, columns, data) => {
      return "\uFEFF" + buildHead(columns) + buildBody(data);
    },
  };

  return (
    <>
      <MUITable title={title} options={options} data={rows} columns={columns} />
      <WriteOffAccountModal
        account={selectedAccount}
        fetchAccounts={fetchAccounts ?? null}
        open={writeOffModalOpen}
        onClose={() => setWriteOffModalOpen(false)}
      />
      <ConfirmModal
        open={confirmRegretModalOpen}
        onClose={() => setConfirmRegretModalOpen(false)}
        title={"Ångra inbetalning"}
      >
        {`Är du säker på att du vill ångra Slutkundsinbetalning ${selectedAccount?.Id}?`}
        <div
          style={{
            display: "flex",
            marginTop: "50px",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <LoadingButton
            variant="contained"
            color="error"
            onClick={executeRegret}
          >
            Ångra
          </LoadingButton>
          <LoadingButton onClick={() => setConfirmRegretModalOpen(false)}>
            Avbryt
          </LoadingButton>
        </div>
      </ConfirmModal>
    </>
  );
}
